import { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Fab,
} from '@mui/material';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import { BRAND_COLORS } from '../theme';

const PWA_INSTALLED_KEY = 'pwa-added-to-home';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isAddedToHomeScreen() {
  if (isStandalone()) return true;

  try {
    return localStorage.getItem(PWA_INSTALLED_KEY) === 'true';
  } catch {
    return false;
  }
}

function markAddedToHomeScreen() {
  try {
    localStorage.setItem(PWA_INSTALLED_KEY, 'true');
  } catch {
    // Storage may be unavailable in private mode.
  }
}

function isIosDevice() {
  const ua = window.navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return ua.includes('Mac') && 'ontouchend' in document;
}

function isAndroidDevice() {
  return /Android/i.test(window.navigator.userAgent);
}

function readCapturedPrompt() {
  return typeof window !== 'undefined' ? window.__deferredInstallPrompt ?? null : null;
}

function installHelpContent() {
  if (isIosDevice()) {
    return {
      title: 'Add to Home Screen',
      body: [
        'Works in Safari, Chrome, Firefox, and other browsers on iPhone and iPad.',
        'Tap the Share button (square with an arrow) in the toolbar.',
        'Scroll and tap “Add to Home Screen”, then confirm.',
      ],
    };
  }
  if (isAndroidDevice()) {
    return {
      title: 'Add to Home screen',
      body: [
        'Works in Chrome, Samsung Internet, Firefox, Edge, and other Android browsers.',
        'Open the browser menu (⋮ or ⋯ in the corner).',
        'Choose “Install app”, “Add to Home screen”, or “Add shortcut to Home” — the label depends on your browser.',
      ],
    };
  }
  return {
    title: 'Install this site',
    body: [
      'Chrome / Edge: open the menu (⋮) and choose Install or Add to Home screen, or use the install icon in the address bar.',
      'Other browsers: use the menu or sharing options your browser offers for installing or creating a shortcut.',
    ],
  };
}

export default function AddToHomeButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(readCapturedPrompt);
  const [helpOpen, setHelpOpen] = useState(false);
  const [addedToHome, setAddedToHome] = useState(isAddedToHomeScreen);

  const syncFromWindow = useCallback(() => {
    setDeferredPrompt(readCapturedPrompt());
  }, []);

  const syncAddedState = useCallback(() => {
    if (isAddedToHomeScreen()) {
      setAddedToHome(true);
    }
  }, []);

  useEffect(() => {
    if (addedToHome) return undefined;

    syncFromWindow();
    syncAddedState();

    const onAppBeforeInstallPrompt = () => syncFromWindow();
    const onAppInstalled = () => {
      markAddedToHomeScreen();
      setAddedToHome(true);
    };

    const displayModeQuery = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = () => syncAddedState();

    window.addEventListener('appbeforeinstallprompt', onAppBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    displayModeQuery.addEventListener('change', onDisplayModeChange);

    return () => {
      window.removeEventListener('appbeforeinstallprompt', onAppBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
      displayModeQuery.removeEventListener('change', onDisplayModeChange);
    };
  }, [addedToHome, syncAddedState, syncFromWindow]);

  const runInstallPrompt = async () => {
    const p = deferredPrompt || readCapturedPrompt();
    if (!p) return;
    p.prompt();
    const { outcome } = await p.userChoice;
    window.__deferredInstallPrompt = null;
    setDeferredPrompt(null);

    if (outcome === 'accepted') {
      markAddedToHomeScreen();
      setAddedToHome(true);
    }
  };

  const handleAddClick = async () => {
    const p = deferredPrompt || readCapturedPrompt();
    if (p) {
      await runInstallPrompt();
      return;
    }
    setHelpOpen(true);
  };

  if (addedToHome) return null;

  const help = installHelpContent();
  const canInstall = Boolean(deferredPrompt || readCapturedPrompt());
  const InstallIcon = canInstall ? InstallMobileIcon : AddToHomeScreenIcon;

  return (
    <>
      <Fab
        variant="extended"
        color="primary"
        onClick={handleAddClick}
        aria-label="Add to Home Screen"
        sx={{
          width: '100%',
          justifyContent: 'center',
          px: 2.5,
          py: 1.5,
          gap: 1,
          fontWeight: 600,
          letterSpacing: 0.2,
          boxShadow: `0 8px 24px ${BRAND_COLORS.primary}59`,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 28px ${BRAND_COLORS.primary}73`,
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }}
      >
        <InstallIcon sx={{ fontSize: 22 }} />
        Install App
      </Fab>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} aria-labelledby="install-help-title">
        <DialogTitle id="install-help-title">{help.title}</DialogTitle>
        <DialogContent>
          {help.body.map((paragraph, i) => (
            <DialogContentText key={i} sx={{ mb: 1 }}>
              {paragraph}
            </DialogContentText>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
